/**
 * Build A Crate — active discount resolution and price math.
 */
(function (global) {
  'use strict';

  var WINDOW_ORDER = ['pre', 'main', 'cyber', 'post'];

  function todayYmd() {
    var d = new Date();
    var month = String(d.getMonth() + 1);
    var day = String(d.getDate());
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return d.getFullYear() + '-' + month + '-' + day;
  }

  function normalizeYmd(value) {
    if (value == null || value === '') return '';
    var match = String(value).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!match) return String(value).trim();
    var month = match[2];
    var day = match[3];
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return match[1] + '-' + month + '-' + day;
  }

  function isDateInRange(start, end, today) {
    var normalizedStart = normalizeYmd(start);
    var normalizedEnd = normalizeYmd(end);
    var normalizedToday = normalizeYmd(today);
    if (!normalizedStart || !normalizedEnd) return false;
    return normalizedStart <= normalizedToday && normalizedToday <= normalizedEnd;
  }

  function parseAmount(value) {
    var amount = parseFloat(value, 10);
    return Number.isFinite(amount) ? amount : 0;
  }

  function isNumericDiscountValue(value) {
    return /^\d+(\.\d{1,2})?$/.test(String(value || '').trim());
  }

  function resolveWindowDiscountAmount(win) {
    if (!win) return 0;
    var amount = parseAmount(win.amount);
    if (amount > 0) return amount;
    if (isNumericDiscountValue(win.code)) {
      return parseAmount(win.code);
    }
    return 0;
  }

  function resolveWindowDiscountCode(win, effectiveAmount) {
    if (!win) return '';
    var configuredAmount = parseAmount(win.amount);
    if (configuredAmount > 0) {
      return String(win.code || '').trim();
    }
    if (isNumericDiscountValue(win.code)) {
      return '';
    }
    return String(win.code || '').trim();
  }

  function applyDiscountToCents(cents, discountType, discountValue) {
    var amount = parseAmount(discountValue);
    if (amount <= 0) return cents;
    if (discountType === 'fix_amount') {
      return cents - Math.round(amount * 100);
    }
    if (discountType === 'Percentage') {
      return Math.round((cents * (100 - amount)) / 100);
    }
    return cents;
  }

  function getDateDiscountWindows(body) {
    if (!body) return null;
    var el = body.querySelector('[data-bac-date-discounts]');
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent.trim());
      return data && Array.isArray(data.windows) ? data.windows : null;
    } catch (err) {
      return null;
    }
  }

  /**
   * Returns the active date-driven discount (pre → main → cyber → post), or null.
   */
  function resolveActiveDateDiscount(body) {
    if (!body) return null;

    var windows = getDateDiscountWindows(body);
    var today = todayYmd();

    if (windows) {
      for (var i = 0; i < WINDOW_ORDER.length; i++) {
        var key = WINDOW_ORDER[i];
        for (var w = 0; w < windows.length; w++) {
          var win = windows[w];
          if (!win || win.key !== key) continue;
          if (!isDateInRange(win.start, win.end, today)) continue;
          var amount = resolveWindowDiscountAmount(win);
          if (win.type && amount > 0) {
            return {
              key: key,
              type: win.type,
              amount: String(amount),
              code: resolveWindowDiscountCode(win, amount)
            };
          }
        }
      }
    }

    var fallbackType = body.getAttribute('data-bac-discount-type');
    var fallbackAmount = body.getAttribute('data-bac-discount-amount');
    if (fallbackType && fallbackAmount && parseAmount(fallbackAmount) > 0) {
      return {
        key: 'server',
        type: fallbackType,
        amount: fallbackAmount,
        code: body.getAttribute('data-bac-discount-code') || ''
      };
    }

    return null;
  }

  function calculatePlanSubtotalCents(body) {
    if (!body) return 0;

    var baseCents = parseInt(body.getAttribute('data-bac-plan-base-price-cents'), 10);
    if (!Number.isFinite(baseCents) || baseCents < 0) {
      var baked = parseInt(body.getAttribute('data-bac-plan-subtotal-cents'), 10);
      return Number.isFinite(baked) ? Math.max(0, baked) : 0;
    }

    var payFreq = parseInt(body.getAttribute('data-bac-plan-pay-frequency'), 10);
    var shipFreq = parseInt(body.getAttribute('data-bac-plan-ship-frequency'), 10);
    var cents = baseCents;

    var activeDateDiscount = resolveActiveDateDiscount(body);
    if (activeDateDiscount) {
      cents = applyDiscountToCents(cents, activeDateDiscount.type, activeDateDiscount.amount);
    }

    return Math.max(0, cents);
  }

  function calculatePerCrateCents(body, subtotalCents) {
    var payFreq = parseInt(body.getAttribute('data-bac-plan-pay-frequency'), 10);
    var shipFreq = parseInt(body.getAttribute('data-bac-plan-ship-frequency'), 10);
    if (!Number.isFinite(payFreq) || payFreq <= 0 || !Number.isFinite(shipFreq) || shipFreq <= 0) {
      return subtotalCents;
    }
    return Math.round((subtotalCents / payFreq) * shipFreq);
  }

  function formatMoney(dollars) {
    return '$' + dollars.toFixed(2);
  }

  function formatActiveDiscountLabel(active) {
    if (!active) return '';
    if (active.type === 'fix_amount') {
      return 'flash sale - $' + active.amount + ' off';
    }
    if (active.type === 'Percentage') {
      return 'flash sale - ' + active.amount + '% off';
    }
    return '';
  }

  function pricesDifferInCents(originalCents, discountedCents) {
    if (!Number.isFinite(originalCents) || !Number.isFinite(discountedCents)) return false;
    return Math.round(originalCents) > Math.round(discountedCents);
  }

  function getPriceDisplayState(body) {
    var baseCents = parseInt(body.getAttribute('data-bac-plan-base-price-cents'), 10);
    if (!Number.isFinite(baseCents) || baseCents < 0) {
      baseCents = 0;
    }
    var finalCents = calculatePlanSubtotalCents(body);
    var originalPerCrateCents = calculatePerCrateCents(body, baseCents);
    var discountedPerCrateCents = calculatePerCrateCents(body, finalCents);
    var hasSubtotalDiscount = pricesDifferInCents(baseCents, finalCents);
    var hasPerCrateDiscount = pricesDifferInCents(originalPerCrateCents, discountedPerCrateCents);

    return {
      hasDiscount: hasSubtotalDiscount,
      hasSubtotalDiscount: hasSubtotalDiscount,
      hasPerCrateDiscount: hasPerCrateDiscount,
      baseCents: baseCents,
      finalCents: finalCents,
      originalPerCrateCents: originalPerCrateCents,
      discountedPerCrateCents: discountedPerCrateCents,
      originalPerCrateDollars: originalPerCrateCents / 100,
      discountedPerCrateDollars: discountedPerCrateCents / 100,
      originalSubtotalDollars: baseCents / 100,
      discountedSubtotalDollars: finalCents / 100
    };
  }

  global.BuildACrateDiscounts = {
    resolveActiveDateDiscount: resolveActiveDateDiscount,
    calculatePlanSubtotalCents: calculatePlanSubtotalCents,
    calculatePerCrateCents: calculatePerCrateCents,
    formatMoney: formatMoney,
    formatActiveDiscountLabel: formatActiveDiscountLabel,
    getPriceDisplayState: getPriceDisplayState,
    pricesDifferInCents: pricesDifferInCents
  };
})(typeof window !== 'undefined' ? window : this);
